import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Workflow, Plus, Pencil, Trash2, Play, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import ProtocolModal from './ProtocolModal';
import ApplyProtocolModal from './ApplyProtocolModal';
import { useConfirm } from '../../components/common/ConfirmDialog';

export default function ProtocolPanel({ onClose, catalogItems, horse }) {
  const [protocols, setProtocols]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [availableHorses, setAvailableHorses] = useState([]);
  const confirm = useConfirm();
  const [locations, setLocations]     = useState([]);

  const [protocolModal, setProtocolModal] = useState({ open: false, editing: null });
  const [applyModal, setApplyModal]       = useState({ open: false, protocol: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes, lRes] = await Promise.all([
        api.get('/farmfolio/protocols'),
        api.get('/farmfolio/global/horses').catch(() => ({ data: [] })),
        api.get('/farmfolio/locations').catch(() => ({ data: [] })),
      ]);
      setProtocols(pRes.data || []);
      setAvailableHorses(hRes.data || []);
      setLocations(lRes.data || []);
    } catch { toast.error('Failed to load protocols'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (p) => {
    if (!await confirm({ title: `Delete protocol "${p.name}"?`, destructive: true })) return;
    try {
      await api.delete(`/farmfolio/protocols/${p.id}`);
      toast.success('Protocol deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Workflow className="w-4 h-4 text-primary" /> Protocols
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setProtocolModal({ open: true, editing: null })}
              className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Create Protocol
            </Button>
            {onClose && (
              <Button size="sm" variant="outline" onClick={onClose}>
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Catalog
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground mb-3">
            Protocols are reusable care schedules that can be applied to any horse.
          </p>

          {loading ? (
            <p className="text-sm text-center py-8 text-muted-foreground">Loading…</p>
          ) : protocols.length === 0 ? (
            <p className="text-sm text-center py-8 text-muted-foreground italic">
              No protocols yet. Click "Create Protocol" to build your first one.
            </p>
          ) : (
            <div className="overflow-x-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold">Name</TableHead>
                    <TableHead className="text-xs font-semibold">Reason</TableHead>
                    <TableHead className="text-xs font-semibold w-20">Items</TableHead>
                    <TableHead className="text-xs font-semibold w-24">Day Range</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {protocols.map(p => {
                    const items   = p.schedule_items || [];
                    const maxDay  = items.length ? Math.max(...items.map(i => i.day_offset || 0)) : 0;
                    const dayRange = items.length ? `Day 0 – ${maxDay}` : '—';
                    return (
                      <TableRow key={p.id} className="text-xs hover:bg-muted/20">
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="text-muted-foreground">{p.reason || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{items.length}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{dayRange}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" className="h-7 text-xs px-2"
                              onClick={() => setApplyModal({ open: true, protocol: p })}
                              title="Apply to horses"
                              data-testid={`protocol-apply-${p.id}`}>
                              <Play className="w-3 h-3 mr-1" /> Apply
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => setProtocolModal({ open: true, editing: p })}
                              title="Edit" data-testid={`protocol-edit-${p.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => handleDelete(p)}
                              title="Delete" data-testid={`protocol-delete-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ProtocolModal
        open={protocolModal.open}
        onClose={() => setProtocolModal({ open: false, editing: null })}
        onSaved={() => { fetchAll(); setProtocolModal({ open: false, editing: null }); }}
        editing={protocolModal.editing}
        catalogItems={catalogItems}
        locations={locations}
      />

      <ApplyProtocolModal
        open={applyModal.open}
        onClose={() => setApplyModal({ open: false, protocol: null })}
        onSaved={() => setApplyModal({ open: false, protocol: null })}
        protocol={applyModal.protocol}
        availableHorses={availableHorses}
        preselectedHorse={horse || null}
      />
    </>
  );
}
